import type { PromoScope, PromoApplyMode } from './promoStorage';

// Newsletter deep-link URL parameters. Kept in one place so the site-wide capture
// (promoCapture, runs from the always-loaded bundle) and the PDP hook (usePromoCode) parse
// them identically. This module has no Preact/DOM-heavy deps so it stays cheap to import.
export const PROMO_PARAM_CODE = 'promo';
export const PROMO_PARAM_SCOPE = 'prsc';
export const PROMO_PARAM_APPLY = 'pra';
export const PROMO_PARAM_ONUMBERS = 'pron'; // ORT-3077 A: o-numbers the code is valid for
                                            // ORT-3152: entries may be wildcard patterns (M*, O611*)
export const PROMO_PARAM_OFFERS = 'prooff'; // ORT-3077 B: "0" suppresses competing Stage-2 offers

const CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
// ORT-3152: `*` is allowed inside an entry so a campaign can cover a whole product range without
// listing every number: `M*` (Essentials), `O*` (Komplexprodukte), `O611*` (all Immun packsizes).
const ONUMBER_PATTERN = /^[A-Za-z0-9*-]{1,32}$/;

/**
 * Does a single ?pron= entry cover this product's o-number?
 *
 * An entry is either a literal o-number (`O611-30`) or a wildcard pattern where `*` stands for any
 * run of characters (`M*`, `O611*`, `ESET*`). Everything outside a `*` is matched literally, and
 * the whole value must match: an unanchored regex would let `M1` hit `O611-M10`.
 *
 * Matching is case-insensitive. The PIM stores o-numbers uppercase, but these links are written by
 * hand in the newsletter tool, so `m*` has to hit `M140-90` too.
 *
 * The literal parts are regex-escaped before the pattern is compiled. ONUMBER_PATTERN already
 * limits entries to `[A-Za-z0-9*-]`, so nothing dangerous can get in today, but the escape keeps
 * that guarantee local to this function instead of depending on a validation rule three lines up.
 */
function matchesONumberPattern(pattern: string, oNumber: string): boolean {
  const needle = oNumber.trim().toUpperCase();
  const parts = pattern.trim().toUpperCase().split('*');

  // No wildcard: plain equality, the pre-ORT-3152 behaviour.
  if (parts.length === 1) return parts[0] === needle;

  const source = parts.map((part) => part.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&')).join('.*');
  return new RegExp(`^${source}$`).test(needle);
}

/**
 * True when ANY entry of a ?pron= list covers this o-number. The list is a permissive union, so
 * `pron=M*,ESET*` reads as "all Essentials, singles and sets".
 */
export function oNumberMatches(patterns: string[], oNumber: string): boolean {
  return patterns.some((pattern) => matchesONumberPattern(pattern, oNumber));
}

export interface ParsedPromoParams {
  code: string;
  scope: PromoScope;
  applyMode: PromoApplyMode;
  oNumbers: string[] | null;
  suppressOffers: boolean;
}

/**
 * Parse the promo deep-link params from a location.search string. Returns null when there is no
 * valid `promo` code (nothing to capture).
 */
export function parsePromoParams(search: string): ParsedPromoParams | null {
  const params = new URLSearchParams(search);
  const code = params.get(PROMO_PARAM_CODE);
  if (!code || !CODE_PATTERN.test(code)) return null;

  const rawScope = params.get(PROMO_PARAM_SCOPE);
  const scope: PromoScope = rawScope === 's' || rawScope === 'd' ? rawScope : 'p';
  const applyMode: PromoApplyMode = params.get(PROMO_PARAM_APPLY) === 'replace' ? 'replace' : 'add';

  const rawONumbers = params.get(PROMO_PARAM_ONUMBERS);
  const oNumbers = rawONumbers
    ? rawONumbers
        .split(',')
        .map((n) => n.trim())
        .filter((n) => ONUMBER_PATTERN.test(n))
    : [];

  // Offers are shown by default; ?prooff=0 opts out (suppress competing Stage-2 offers).
  const suppressOffers = params.get(PROMO_PARAM_OFFERS) === '0';

  return { code, scope, applyMode, oNumbers: oNumbers.length > 0 ? oNumbers : null, suppressOffers };
}
